import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { LoadingScreen } from '@/components/LoadingScreen';
import { useAuth } from '@/hooks/useAuth';

export function RequireAuth({ component: Component }: { component: any }) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [location, setLocation] = useLocation();

  // Fetch user data for super admin check
  const { data: userData, isLoading: isUserLoading } = useQuery<any>({
    queryKey: ['/api/auth/me'],
    enabled: isAuthenticated,
  });

  // Fetch user's organizations to determine if they need onboarding
  const { data: userOrgs, isLoading: isOrgsLoading } = useQuery<Array<any>>({
    queryKey: ['/api/users/me/organizations'],
    enabled: isAuthenticated && !!user && !user.partnerId,
  });

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Route unauthenticated users to login to avoid protected-route dead ends.
      // useAuth() is configured with retry: false and 401 -> null, which prevents auth retry loops.
      const next = encodeURIComponent(location || '/');
      setLocation(`/login?next=${next}`);
    }
  }, [isLoading, isAuthenticated, location, setLocation]);

  // Hard redirect logic for new user onboarding
  useEffect(() => {
    if (isAuthenticated && !isUserLoading && userData?.partnerId) {
      if (!location.startsWith('/partner')) {
        setLocation('/partner');
      }
      return;
    }

    if (isAuthenticated && !isOrgsLoading && !isUserLoading && userOrgs && userData) {
      // Skip onboarding redirects for super admins
      if (userData.isSuperAdmin) {
        return;
      }

      // Whitelist routes that new users (0 orgs) can access during onboarding
      const onboardingAllowedRoutes = ['/welcome', '/onboarding'];
      const isOnOnboardingRoute = onboardingAllowedRoutes.some(route => location.startsWith(route));

      // New user with no orgs - redirect to /welcome (unless on allowed onboarding route)
      if (userOrgs.length === 0 && !isOnOnboardingRoute) {
        setLocation('/welcome');
      }

      // User with orgs on /welcome page - redirect to home
      if (userOrgs.length > 0 && location === '/welcome') {
        setLocation('/');
      }
    }
  }, [isAuthenticated, isOrgsLoading, isUserLoading, userOrgs, userData, location, setLocation]);

  if (isLoading || isOrgsLoading || isUserLoading) {
    const message = isUserLoading
      ? 'Verifying your identity...'
      : isOrgsLoading
        ? 'Setting up your workspace...'
        : 'Loading...';

    return <LoadingScreen message={message} />;
  }

  if (!isAuthenticated) {
    return null;
  }

  return <Component />;
}
